import { BaseService } from './base';
import { Organization } from './auth';

export interface OrganizationMember {
  id: string;
  user_id: string;
  email: string;
  role: 'admin' | 'manager' | 'operator' | 'viewer';
  joined_at: string;
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
  invitation_url?: string;
}

class OrganizationService extends BaseService {
  async getOrganizations(): Promise<Organization[]> {
    const response = await this.makeRequest<Organization[]>('/organizations');
    return response || [];
  }

  async getCurrentOrganization(): Promise<Organization> {
    return await this.makeRequest<Organization>('/organizations/current');
  }

  async createOrganization(name: string): Promise<Organization> {
    return await this.makeRequest<Organization>('/organizations', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async switchOrganization(organizationId: string): Promise<void> {
    await this.makeRequest('/organizations/switch', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    });
  }

  async updateOrganization(organizationId: string, updates: { name?: string; settings?: any; approval_required?: boolean }): Promise<{ organization: Organization }> {
    return await this.makeRequest(`/organizations/${organizationId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async getMembers(organizationId: string): Promise<OrganizationMember[]> {
    return await this.makeRequest<OrganizationMember[]>(
      `/organizations/${organizationId}/members`
    );
  }

  async inviteMember(organizationId: string, email: string, role: string): Promise<{ invitation: { invitation_url: string } }> {
    return await this.makeRequest(`/organizations/${organizationId}/members/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async removeMember(organizationId: string, memberId: string): Promise<void> {
    await this.makeRequest(`/organizations/${organizationId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async updateMemberRole(organizationId: string, memberId: string, role: string): Promise<void> {
    await this.makeRequest(`/organizations/${organizationId}/members/${memberId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async getInvitations(organizationId: string): Promise<OrganizationInvitation[]> {
    return await this.makeRequest<OrganizationInvitation[]>(
      `/organizations/${organizationId}/invitations`
    );
  }

  async cancelInvitation(invitationId: string): Promise<void> {
    await this.makeRequest(`/organizations/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  async validateInvitation(token: string): Promise<{ 
    organization: Organization; 
    role: string; 
    email: string; 
  }> {
    return await this.makeRequest<{ 
      organization: Organization; 
      role: string; 
      email: string; 
    }>(`/organizations/invitations/validate/${token}`);
  }

  async acceptInvitation(token: string): Promise<void> {
    await this.makeRequest('/organizations/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // Amazon Export Integration
  async getAmazonSettings(): Promise<{ amazon_enabled: boolean }> {
    return await this.makeRequest('/organizations/amazon-settings');
  }

  async updateAmazonSettings(enabled: boolean): Promise<{ amazon_enabled: boolean }> {
    return await this.makeRequest('/organizations/amazon-settings', {
      method: 'PUT',
      body: JSON.stringify({ amazon_enabled: enabled }),
    });
  }

  // RetailOS Integration
  async getRetailOSSettings(): Promise<{ retailos_api_key: string | null; retailos_configured: boolean }> {
    return await this.makeRequest('/organizations/retailos-settings');
  }

  async updateRetailOSSettings(apiKey: string | null): Promise<{ retailos_api_key: string | null; retailos_configured: boolean }> {
    return await this.makeRequest('/organizations/retailos-settings', {
      method: 'PUT',
      body: JSON.stringify({ retailos_api_key: apiKey }),
    });
  }
}

export const organizationService = new OrganizationService();